import { SwitchProps, Theme } from "@mui/material";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import LockIcon from "@mui/icons-material/Lock";
import { JSX } from "react";
import { PermissionToggle } from "permissions/_views/tables/_components/permission-toggle";
import {
  selectEffectivePermission,
  makeVirtualPermission,
  toCardMeta,
  getCardStyle,
  getResourceBaseColor,
} from "permissions/_views/tables/_utilities/utils";
import {
  PackedItem,
  PermissionCardHeader,
  PermissionTitleContainer,
  Center,
  GroupedPermissionActions,
  GroupedPermissionsContainer,
  SmallSelect,
} from "permissions/_views/tables/permissions-table-styles";
import Permission from "permissions/permission";
import type { Resource } from "permissions/resources/permission-resource";
import {
  UniversalCellConfig,
  CardType,
  CategoryLabel,
  ControlMeta,
  CardMeta,
} from "permissions/_views/types/permission-types";
import { useSession } from "users/__session/session-context";
import PermissionsByGrantee from "permissions/by-grantee/permissions-by-grantee";

export type GroupedPermissionCardProps = {
  row: PermissionsByGrantee;
  config: Extract<UniversalCellConfig, { cardType: CardType.Grouped }>;
  isSingleGranteeView: boolean;
  isCreateTemplateView: boolean;
  isManageTemplatesView: boolean;
  theme: Theme;
  activeCategory: CategoryLabel | "";
  onToggle(permission: Permission | undefined): void;
  onApplyMeta(effective: Permission, resource: Resource, meta: ControlMeta, newValue: string): void;
  getCardSubheader(resource: Resource): string | undefined;
};

export default function GroupedPermissionCard(props: Readonly<GroupedPermissionCardProps>): JSX.Element {
  const {
    row,
    config,
    isSingleGranteeView,
    isCreateTemplateView,
    isManageTemplatesView,
    theme,
    activeCategory,
    onToggle,
    onApplyMeta,
    getCardSubheader,
  } = props;

  const session = useSession();

  const items: CardMeta[] = config.keys
    .map((key) => {
      const permission =
        selectEffectivePermission(config.candidatesForKey(row, key) as Permission[]) ??
        (config.templateForKey
          ? (() => {
              const template = config.templateForKey(key);
              return template ? makeVirtualPermission(row.granteeUserId, template) : undefined;
            })()
          : undefined);

      if (!permission) return null;
      const control = config.controlMetaForResource(permission.resource, key);
      return toCardMeta(permission, key, control);
    })
    .filter(Boolean) as CardMeta[];

  if (!items.length) return <Icon>remove</Icon>;

  const resource = items[0].resource;
  const cardTitle = config.title;
  const cardSubHeader = getCardSubheader(resource);
  const colSpan = config.colSpan ?? 2;
  const rowSpan = config.rowSpan ?? 2;

  const isOwnPermission: boolean = row.granteeUserId.isEqualTo(session.user?.id);
  const isDisabled = items.every((i) => i.permission?.isDisabled);

  const { bgColor, borderColor, display, textColor, selectSx } = getCardStyle(
    theme,
    resource,
    isDisabled,
    activeCategory
  );

  return (
    <PackedItem
      key={config.title ?? "group"}
      isSingleGranteeView={isSingleGranteeView}
      isCreateTemplateView={isCreateTemplateView}
      isManageTemplatesView={isManageTemplatesView}
      cols={colSpan}
      rows={rowSpan}
      sx={{ backgroundColor: bgColor, borderColor, color: textColor, display }}
      variant="outlined"
    >
      {(isSingleGranteeView || config.title || cardSubHeader) && (
        <PermissionCardHeader
          grouped={true}
          title={<PermissionTitleContainer grouped={true}>{cardTitle}</PermissionTitleContainer>}
          subheader={<>{cardSubHeader !== cardTitle && <span title={cardSubHeader}>{cardSubHeader}</span>}</>}
          slotProps={{
            subheader: {
              sx: {
                maxWidth: "150px",
                overflow: "hidden",
                textAlign: "left",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              },
            },
          }}
        />
      )}

      <Center>
        <GroupedPermissionActions isSingleGranteeView={isSingleGranteeView} isCreateTemplateView={isCreateTemplateView}>
          <GroupedPermissionsContainer
            sx={{
              gap: 1,
              gridTemplateColumns: `auto 1fr ${colSpan > 1 ? "2fr" : ""}`,
              textAlign: colSpan < 2 ? "right" : "left",
            }}
          >
            {items.map(({ key, permission, resource, isVirtual, control }) => {
              const disabled = permission?.isDisabled;
              return (
                <div style={{ display: "contents" }} key={key}>
                  <Tooltip title={isOwnPermission ? "You cannot change your own permissions" : ""} disableInteractive>
                    <span>
                      {isOwnPermission ? (
                        <LockIcon fontSize="small" sx={{ mr: 1 }} />
                      ) : (
                        <PermissionToggle
                          checked={!isVirtual && !permission?.disabledDate}
                          disabled={isOwnPermission}
                          onChange={() => onToggle(permission)}
                          color={getResourceBaseColor(resource) as SwitchProps["color"]}
                        />
                      )}
                    </span>
                  </Tooltip>
                  <div style={{ fontWeight: 500 }}>{config.labelForKey(key)}</div>
                  {control && (
                    <SmallSelect
                      size="small"
                      disabled={isVirtual || disabled || !permission || isOwnPermission}
                      sx={selectSx}
                      value={control.value}
                      onChange={(e) => onApplyMeta(permission!, resource, control, String(e.target.value))}
                    >
                      {control.options.map((k) => (
                        <MenuItem key={k} value={k}>
                          {k}
                        </MenuItem>
                      ))}
                    </SmallSelect>
                  )}
                </div>
              );
            })}
          </GroupedPermissionsContainer>
        </GroupedPermissionActions>
      </Center>
    </PackedItem>
  );
}
